import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, Video, Globe } from 'lucide-react';
import { toast } from 'sonner';
import './quote.css';

/* ── Dropdown options ── */
const VIDEO_TYPES = ['Film de marque', 'Publicité / Spot', 'Contenu réseaux sociaux', 'Aftermovie', 'Autre'];
const VIDEO_DURATIONS = ['< 30s', '30s – 1 min', '1 – 3 min', '3 – 5 min', '5 min +'];
const VIDEO_DEADLINES = ['Flexible', '< 1 mois', '1 – 3 mois', '3 – 6 mois'];
const BUDGET_OPTIONS = ['Non défini', '< 2 000 €', '2 000 – 5 000 €', '5 000 – 10 000 €', '10 000 € +'];

const WEB_TYPES = ['Landing page', 'Site vitrine', 'E-commerce', 'Application web', 'Autre'];
const WEB_PAGES = ['1 – 3', '4 – 7', '8 – 15', '15 +'];
const WEB_NEEDS = ['Design UI/UX', 'Responsive', 'CMS / Admin', 'SEO', 'Animations', 'Multilingue', 'Intégration API'];

/* ── Component ── */
export const QuoteModal = ({ context, onClose }) => {
  // context: 'video' | 'web' | null (user chooses)
  const [type, setType] = useState(context || null);
  const [sent, setSent] = useState(false);

  // Common fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');

  // Video fields
  const [videoType, setVideoType] = useState('');
  const [videoDuration, setVideoDuration] = useState('');
  const [videoDeadline, setVideoDeadline] = useState('');
  const [videoBudget, setVideoBudget] = useState('');

  // Web fields
  const [webType, setWebType] = useState('');
  const [webPages, setWebPages] = useState('');
  const [webNeeds, setWebNeeds] = useState([]);
  const [webBudget, setWebBudget] = useState('');

  // ESC to close
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Lock body scroll when modal open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const toggleNeed = useCallback((need) => {
    setWebNeeds(prev =>
      prev.includes(need) ? prev.filter(n => n !== need) : [...prev, need]
    );
  }, []);

  const [submitting, setSubmitting] = useState(false);
  const canSubmit = email.trim().length > 0 && type !== null && !submitting;

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    try {
      const body = {
        name, email, phone, message,
        serviceType: type,
        ...(type === 'video' ? { videoType, videoDuration, videoDeadline, videoBudget } : {}),
        ...(type === 'web' ? { webType, webPages, webNeeds, webBudget } : {}),
      };

      const res = await fetch('/api/quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.ok || !data.sent) {
        throw new Error(data.error || 'Envoi échoué — veuillez réessayer.');
      }

      setSent(true);
    } catch (err) {
      console.error('[QuoteModal] Submit error:', err);
      toast.error(err.message || 'Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  }, [canSubmit, name, email, phone, message, type, videoType, videoDuration, videoDeadline, videoBudget, webType, webPages, webNeeds, webBudget]);

  const showTypeSelector = context === null && !sent;

  return createPortal(
    <AnimatePresence>
      <motion.div
        className="quote-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-label="Demande de devis"
        data-testid="quote-modal-overlay"
        data-quote-modal="true"
      >
        <motion.div
          className="quote-modal"
          initial={{ scale: 0.94, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.96, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 28 }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="quote-modal__close"
            onClick={onClose}
            aria-label="Fermer"
            data-testid="quote-modal-close"
          >
            <X size={16} strokeWidth={1.5} />
          </button>

          {sent ? (
            /* ── Success state ── */
            <div className="quote-success">
              <div className="quote-success__icon" aria-hidden="true">&#10003;</div>
              <h2 className="quote-success__title">Message envoy&eacute; !</h2>
              <p className="quote-success__text">
                Nous reviendrons vers vous sous 24–48h.
              </p>
            </div>
          ) : (
            /* ── Form ── */
            <form onSubmit={handleSubmit}>
              {/* Header */}
              <div className="quote-modal__header">
                <h2 className="quote-modal__title">Parlons de votre projet</h2>
                <p className="quote-modal__subtitle">
                  R&eacute;ponse sous 24–48h. Aucun engagement.
                </p>
              </div>

              {/* Type selector (only when no context passed) */}
              {showTypeSelector && (
                <div className="quote-type-selector">
                  <button
                    type="button"
                    className={`quote-type-btn ${type === 'video' ? 'quote-type-btn--active' : ''}`}
                    onClick={() => setType('video')}
                  >
                    <Video size={14} strokeWidth={1.5} />
                    Vid&eacute;o
                  </button>
                  <button
                    type="button"
                    className={`quote-type-btn ${type === 'web' ? 'quote-type-btn--active' : ''}`}
                    onClick={() => setType('web')}
                  >
                    <Globe size={14} strokeWidth={1.5} />
                    Web / App
                  </button>
                </div>
              )}

              {/* Common fields */}
              <div className="quote-field">
                <label className="quote-field__label">Nom / Entreprise</label>
                <input
                  className="quote-field__input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jean Dupont"
                  autoComplete="name"
                />
              </div>
              <div className="quote-field">
                <label className="quote-field__label">Email *</label>
                <input
                  className="quote-field__input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jean@entreprise.com"
                  required
                  autoComplete="email"
                />
              </div>
              <div className="quote-field">
                <label className="quote-field__label">T&eacute;l&eacute;phone</label>
                <input
                  className="quote-field__input"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+33 6 00 00 00 00"
                  autoComplete="tel"
                />
              </div>

              {/* ── Video conditional fields ── */}
              {type === 'video' && (
                <>
                  <div className="quote-section-label">D&eacute;tails vid&eacute;o</div>
                  <div className="quote-field">
                    <label className="quote-field__label">Type de vid&eacute;o</label>
                    <select
                      className="quote-field__select"
                      value={videoType}
                      onChange={(e) => setVideoType(e.target.value)}
                    >
                      <option value="">S&eacute;lectionner...</option>
                      {VIDEO_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="quote-field">
                    <label className="quote-field__label">Dur&eacute;e estim&eacute;e</label>
                    <select
                      className="quote-field__select"
                      value={videoDuration}
                      onChange={(e) => setVideoDuration(e.target.value)}
                    >
                      <option value="">S&eacute;lectionner...</option>
                      {VIDEO_DURATIONS.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div className="quote-field">
                    <label className="quote-field__label">Deadline</label>
                    <select
                      className="quote-field__select"
                      value={videoDeadline}
                      onChange={(e) => setVideoDeadline(e.target.value)}
                    >
                      <option value="">S&eacute;lectionner...</option>
                      {VIDEO_DEADLINES.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div className="quote-field">
                    <label className="quote-field__label">Budget (optionnel)</label>
                    <select
                      className="quote-field__select"
                      value={videoBudget}
                      onChange={(e) => setVideoBudget(e.target.value)}
                    >
                      <option value="">S&eacute;lectionner...</option>
                      {BUDGET_OPTIONS.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                </>
              )}

              {/* ── Web conditional fields ── */}
              {type === 'web' && (
                <>
                  <div className="quote-section-label">D&eacute;tails web / app</div>
                  <div className="quote-field">
                    <label className="quote-field__label">Type de projet</label>
                    <select
                      className="quote-field__select"
                      value={webType}
                      onChange={(e) => setWebType(e.target.value)}
                    >
                      <option value="">S&eacute;lectionner...</option>
                      {WEB_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="quote-field">
                    <label className="quote-field__label">Pages / &eacute;crans</label>
                    <select
                      className="quote-field__select"
                      value={webPages}
                      onChange={(e) => setWebPages(e.target.value)}
                    >
                      <option value="">S&eacute;lectionner...</option>
                      {WEB_PAGES.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div className="quote-field">
                    <label className="quote-field__label">Besoins cl&eacute;s</label>
                    <div className="quote-needs">
                      {WEB_NEEDS.map(need => (
                        <button
                          key={need}
                          type="button"
                          className={`quote-need-pill ${webNeeds.includes(need) ? 'quote-need-pill--active' : ''}`}
                          onClick={() => toggleNeed(need)}
                        >
                          {need}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="quote-field">
                    <label className="quote-field__label">Budget (optionnel)</label>
                    <select
                      className="quote-field__select"
                      value={webBudget}
                      onChange={(e) => setWebBudget(e.target.value)}
                    >
                      <option value="">S&eacute;lectionner...</option>
                      {BUDGET_OPTIONS.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                </>
              )}

              {/* Message (always shown) */}
              <div className="quote-field">
                <label className="quote-field__label">Message</label>
                <textarea
                  className="quote-field__textarea"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="D&eacute;crivez votre projet..."
                />
              </div>

              {/* Footer */}
              <div className="quote-footer">
                <button
                  type="button"
                  className="quote-btn-cancel"
                  onClick={onClose}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="quote-btn-submit"
                  disabled={!canSubmit}
                >
                  {submitting ? 'Envoi...' : 'Envoyer'}
                  {!submitting && <ArrowRight size={14} strokeWidth={1.5} />}
                </button>
              </div>

              <p className="quote-gdpr">
                Vos informations servent uniquement &agrave; r&eacute;pondre &agrave; votre demande
                et sont conserv&eacute;es 3&nbsp;ans au plus apr&egrave;s notre dernier &eacute;change.
                Prestations r&eacute;serv&eacute;es aux professionnels.{' '}
                <a href="/confidentialite" target="_blank" rel="noopener">Donn&eacute;es personnelles et vos droits</a>
              </p>
            </form>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};
